// React
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";

// Types
import { RecipeFieldTypes } from "src/types/contentfulTypes";

// Services
import { useRecipes } from "src/hooks/useRecipes";

// Components
import Loader from "src/components/atoms/loader/Loader";
import NotFound from "src/components/organisms/notFound/NotFound";
import RecipeMeta from "src/components/atoms/recipeMeta/RecipeMeta";
import RecipeHeader from "src/components/molecules/recipeHeader/RecipeHeader";
import RecipeSteps from "src/components/molecules/recipeSteps/RecipeSteps";
import RecipeInfo from "src/components/molecules/recipeInfo/RecipeInfo";

// Styles
import "./RecipeSingle.scss";

const RecipeSingle = () => {
  const [recipe, setRecipe] = useState();
  const params = useParams();
  const { isLoading, data: recipes }: { data: any; isLoading: boolean } =
    useRecipes();

  // Set recipe from url
  useEffect(() => {
    if (!isLoading && recipes) {
      const recipe = recipes.find(
        (recipe: RecipeFieldTypes) => recipe.slug === params.slug
      );
      setRecipe(recipe);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // if still loading, show a loader
  if (isLoading) {
    return <Loader />;
  }

  // if no recipe, show an error state
  if (!recipe) {
    return <NotFound message={"No recipe found with this name"} />;
  }

  // grab the properties
  const {
    name,
    heroImage,
    teaser,
    tags,
    ingredients,
    steps,
    cooking_time,
    serves,
    slug,
  } = recipe as RecipeFieldTypes;

  return (
    <>
      <Helmet>
        <title>{`${name} | Recipe Finder`}</title>
        <meta
          name="description"
          content={teaser || `Recipe details and steps for ${name}.`}
        />
      </Helmet>

      <RecipeMeta name={name} heroImage={heroImage} teaser={teaser} />

      <RecipeHeader name={name} teaser={teaser} />

      <RecipeInfo
        heroImage={heroImage}
        ingredients={ingredients}
        serves={serves}
        cooking_time={cooking_time}
        tags={tags}
        slug={slug}
      />

      <div className="bg-flat--large shadow--light">
        <div className="container">
          <RecipeSteps steps={steps} />
        </div>
      </div>
    </>
  );
};

export default RecipeSingle;
